import { ApolloLink } from '@apollo/client';
import { OperationTypeNode } from 'graphql';
import { from, mergeMap } from 'rxjs';

import { queriesSentWithoutDeviceFingerprint } from '#src/contexts/deviceFingerprint/index.ts';
import { deviceFingerprintRef } from '#src/lib/http.ts';
import { localStorage } from '#src/lib/storage/localStorage.ts';

const MUTATION_FINGERPRINT_TIMEOUT_MS = 5000;

const withDeviceFingerprint = (
  operation: ApolloLink.Operation,
  deviceFingerprint: string
) => {
  if (deviceFingerprint) {
    operation.setContext(
      ({ headers }: { headers?: Record<string, string> }) => ({
        headers: { ...headers, DEVICE_FINGERPRINT: deviceFingerprint },
      })
    );
  }
  return operation;
};

const fingerprintOrEmpty = async (generate: () => Promise<string>) =>
  Promise.race([
    generate().catch(() => ''),
    new Promise<string>(resolve => {
      setTimeout(() => resolve(''), MUTATION_FINGERPRINT_TIMEOUT_MS);
    }),
  ]);

// The backend reads DEVICE_FINGERPRINT in mutations (sign-in, sign-up, OAuth,
// device confirmation, wallet), in signed-in CurrentUser/UserWallet fields and
// in the Devise REST endpoints (lib/http.ts). Mutations wait for it, capped so
// a stuck FingerprintJS never blocks them; queries send it only when already
// known, so a fresh visitor (Googlebot included) never waits.
export const createDeviceFingerprintLink = (
  generateDeviceFingerprint: () => Promise<string>
) =>
  new ApolloLink((operation, forward) => {
    if (operation.operationType === OperationTypeNode.MUTATION) {
      return from(fingerprintOrEmpty(generateDeviceFingerprint)).pipe(
        mergeMap(value => forward(withDeviceFingerprint(operation, value)))
      );
    }
    const known =
      deviceFingerprintRef.current ||
      localStorage.getItem('DEVICE_FINGERPRINT') ||
      '';
    if (!known && operation.operationType === OperationTypeNode.QUERY) {
      queriesSentWithoutDeviceFingerprint.current = true;
    }
    return forward(withDeviceFingerprint(operation, known));
  });
